import React from 'react';
import { FormattedMessage } from 'react-intl';

import { TRADE_DIRECTION_BUY, TRADE_DIRECTION_SELL } from 'src/util/constants';
import { PropertyAnchor } from 'src/util/links/property';

/**
 * Renders a property reference for a trade point.
 *
 * - Returns `null` when the trade point has no property, or the property has no
 *   resolvable label (neither title nor uuid).
 * - When `isPopover` is `true`, wraps the label in a `<strong>` tag (plain text,
 *   no navigation — suitable for popovers where an anchor would be inappropriate).
 * - Otherwise, returns a `<PropertyAnchor>` (from `src/util/links/property`)
 *   directly when a uuid is available, or falls back to a plain `<span>` with the
 *   label when uuid is absent.
 * @param {object} tradePoint
 * @param {boolean} isPopover
 * @returns {React.ReactElement|null} `<PropertyAnchor>`, `<strong>`, or `<span>` element, or null.
 */
export const buildPropertyReference = (tradePoint, isPopover) => {
  const property = tradePoint?.meter?.property;
  if (!property) {
    return null;
  }

  const { uuid, title } = property;
  const label = !title ? uuid : title;

  if (!label || label === '') {
    return null;
  }

  if (isPopover) {
    return <strong>{label}</strong>;
  }

  if (uuid) {
    return (
      <PropertyAnchor uuid={uuid}>{label}</PropertyAnchor>
    );
  }

  return <span>{label}</span>;
};

/**
 * Returns a class name when there are no trade rules.
 * Used for targeting components while launching product tours
 * @param {object} tradeRules
 * @param {string} tradeType
 * @returns {string} - class name.
 */
export const emptyTradeRulesClassName = (tradeRules, tradeType) => {
  if (tradeRules && tradeType) {
    const { buy, sell } = tradeRules;
    if (buy?.length === 0 && sell?.length === 0) {
      return ` no-trade-rules-${tradeType}`;
    }
  }
  return '';
};

/**
 * Returns a class name when there are any buy trade rule to prioritize.
 * Used for targeting components while launching product tours
 * @param {object} tradeRules
 * @param {string} tradeType
 * @returns {string} - class name.
 */
export const prioritizeTradesClassName = (tradeRules, tradeType) => {
  if (tradeRules && tradeType) {
    const { buy } = tradeRules;
    if (buy?.length > 0) {
      return ` prioritize-trade-rules-${tradeType}`;
    }
  }
  return '';
};

/**
 * Build a label for a trade rule meter.
 * @param {object} trader
 * @param {object} counterparty
 * @param {string} direction
 * @param {boolean} isPopover
 * @returns {React.ReactElement} - trade rule meter label.
 */
export const getTradeRuleMeterLabel = (
  trader,
  counterparty,
  direction,
  isPopover,
) => {
  if (!trader) {
    return null;
  }
  const { tradePoint } = trader;
  const { uuid: tradePointId, meter } = tradePoint || {};
  const { uuid: counterpartyTradepointId } = counterparty?.tradePoint || {};

  const counterpartyCheck = tradePointId === counterpartyTradepointId;
  const finalCounterparty = counterpartyCheck ? '' : counterparty;

  const { title, identifier } = meter || {};
  const propertyReference = buildPropertyReference(tradePoint, isPopover);
  const {
    title: counterpartyTitle,
    identifier: counterpartyIdentifier,
  } = finalCounterparty?.tradePoint?.meter || {};
  const { tradePoint: counterpartyTradePoint } = finalCounterparty || {};
  const counterpartyPropertyReference = buildPropertyReference(counterpartyTradePoint, isPopover);
  const counterpartyMeterExist = counterpartyTitle && counterpartyIdentifier;

  if (meter) {
    if (!propertyReference) {
      return (
        <FormattedMessage
          id="helpers.trade_helpers.get_trade_rule_meter_label.no_property.label"
          defaultMessage="{title} ({identifier})"
          values={{
            identifier,
            title,
          }}
        />
      );
    }
    if (propertyReference && !counterpartyMeterExist) {
      return (
        <FormattedMessage
          id="helpers.trade_helpers.get_trade_rule_meter_label.with_property.label"
          defaultMessage="{title} ({identifier}) at {propertyReference}"
          values={{
            identifier,
            propertyReference,
            title,
          }}
        />
      );
    }
    if (propertyReference && counterpartyMeterExist && !counterpartyPropertyReference) {
      if (direction === TRADE_DIRECTION_BUY) {
        return (
          <FormattedMessage
            id="helpers.trade_helpers.get_trade_rule_meter_label.trade_direction_buy.with_property_and_counterparty_meter.label"
            defaultMessage="{title} ({identifier}) at {propertyReference} from {counterpartyTitle} ({counterpartyIdentifier})"
            values={{
              counterpartyIdentifier,
              counterpartyTitle,
              identifier,
              propertyReference,
              title,
            }}
          />
        );
      }
      if (direction === TRADE_DIRECTION_SELL) {
        return (
          <FormattedMessage
            id="helpers.trade_helpers.get_trade_rule_meter_label.trade_direction_sell.with_property_and_counterparty_meter.label"
            defaultMessage="{title} ({identifier}) at {propertyReference} to {counterpartyTitle} ({counterpartyIdentifier})"
            values={{
              counterpartyIdentifier,
              counterpartyTitle,
              identifier,
              propertyReference,
              title,
            }}
          />
        );
      }
    }
    if (propertyReference && counterpartyMeterExist && counterpartyPropertyReference) {
      if (direction === TRADE_DIRECTION_BUY) {
        return (
          <FormattedMessage
            id="helpers.trade_helpers.get_trade_rule_meter_label.trade_direction_buy.with_property_and_counterparty_meter_and_counterparty_property.label"
            defaultMessage="{title} ({identifier}) at {propertyReference} from {counterpartyTitle} ({counterpartyIdentifier}) at {counterpartyPropertyReference}"
            values={{
              counterpartyIdentifier,
              counterpartyTitle,
              counterpartyPropertyReference,
              identifier,
              propertyReference,
              title,
            }}
          />
        );
      }
      if (direction === TRADE_DIRECTION_SELL) {
        return (
          <FormattedMessage
            id="helpers.trade_helpers.get_trade_rule_meter_label.trade_direction_sell.with_property_and_counterparty_meter_and_counterparty_property.label"
            defaultMessage="{title} ({identifier}) at {propertyReference} to {counterpartyTitle} ({counterpartyIdentifier}) at {counterpartyPropertyReference}"
            values={{
              counterpartyIdentifier,
              counterpartyTitle,
              counterpartyPropertyReference,
              identifier,
              propertyReference,
              title,
            }}
          />
        );
      }
    }
  }
  return null;
};
